import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";

import { LayerAPI } from "@ai360/core";

import { DialogBox } from "~/core/components/dialog-box/dialog-box";
import { DialogBoxFooterType } from "~/core/components/dialog-box/dialog-box-footer";
import { SelectInput } from "~/core/components/select-input/select-input";
import { ACTIVE_YN } from "~/core/picklist";

import * as selectors from "~/action-panel/components/layer-module/components/layer-list/selectors";
import {
    colorOptionRenderer,
    getBackgroundGradientStyle,
} from "~/action-panel/components/layer-module/utils";

import { messages } from "~/action-panel/components/layer-module/components/i18n-messages";
import "./sampling-zone-info.css";
import { useAppSelector } from "~/store/hooks";

interface IEditColorRampProps {
    isOpen: boolean;
    onSave: (evt: { colorRampGuid: string }) => void;
    onClose: () => void;
}

const EditColorRamp = (props: IEditColorRampProps): JSX.Element => {
    const { isOpen, onSave, onClose } = props;
    const intl = useIntl();
    //state variables
    const colorRamps: LayerAPI.IColorRamp[] = useAppSelector(selectors.getColorRamps);
    const [colorRampGuid, setColorRampGuid] = useState<string>(null);
    const [colorRampSelectOptions, setColorRampSelectOptions] =
        useState<{ background: string; label: string; value: string }[]>();
    const [backgroundImageStyle, setBackgroundImageStyle] = useState<string>();

    // #region useEffects
    useEffect(() => {
        if (colorRamps) {
            const soilTypeColorRamp = colorRamps.find((cr) => cr.colorRampName === "Soil Type");
            setColorRampSelectOptions(
                colorRamps.map((cr) => ({
                    background: getBackgroundGradientStyle(cr.layerFiles.slice(-1)[0]),
                    label: " ",
                    titleValue: cr.colorRampName,
                    value: cr.colorRampGuid,
                }))
            );
            setBackgroundImageStyle(
                getBackgroundGradientStyle(soilTypeColorRamp.layerFiles.slice(-1)[0])
            );
            setColorRampGuid(soilTypeColorRamp.colorRampGuid);
        }
    }, [colorRamps]);

    useEffect(() => {
        if (!colorRampGuid) {
            return;
        }
        const colorRamp = colorRamps.find((cs) => cs.colorRampGuid === colorRampGuid);

        setColorRampSelectOptions(
            colorRamps.map((cr) => ({
                background: getBackgroundGradientStyle(cr.layerFiles.slice(-1)[0]),
                label: " ",
                titleValue: cr.colorRampName,
                value: cr.colorRampGuid,
            }))
        );

        setBackgroundImageStyle(
            colorRamp && getBackgroundGradientStyle(colorRamp.layerFiles.slice(-1)[0])
        );
    }, [colorRampGuid]);
    // #endregion

    // #region event handlers
    const updateColorRamp = (colorRampGuid: string): void => {
        setColorRampGuid(colorRampGuid);
    };

    const onDialogSave = () => {
        onSave({
            colorRampGuid: colorRampGuid,
        });
    };
    // #endregion

    const { formatMessage } = intl;
    return (
        <DialogBox
            className="surface-properties-dialog"
            draggable
            footerType={DialogBoxFooterType.ACTION_CANCEL}
            forceOverflow
            isOpen={isOpen}
            letterIcon={formatMessage(messages.layerTypeSoil)}
            onAction={onDialogSave}
            onClose={onClose}
            title={"Color Ramp"}
            actionDisabled={false}
            unrestricted
        >
            <div className="color-ramp-section">
                <div className="inputs-section">
                    <SelectInput
                        inputCSS={{ backgroundImage: backgroundImageStyle }}
                        clearable={false}
                        filterable={false}
                        onChange={(e) => updateColorRamp(e)}
                        optionIsHiddenKey={ACTIVE_YN}
                        options={colorRampSelectOptions}
                        optionRenderer={colorOptionRenderer}
                        placeholderText={formatMessage(messages.colorRamp)}
                        value={colorRampGuid}
                    />
                </div>
            </div>
        </DialogBox>
    );
};

export default EditColorRamp;
